import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Trash2, ShoppingBag, Minus, Plus, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Link, useNavigate } from "react-router-dom";
import { usePurchaseQuote, QuoteSummary, money } from "./PurchaseReview";

const CartDrawer = () => {
  const {
    cart,
    itemCount,
    subtotal,
    drawerOpen,
    setDrawerOpen,
    updateQuantity,
    removeItem,
    clearCart,
  } = useCart();
  const { token, isAuthenticated } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const navigate = useNavigate();
  const quoteState = usePurchaseQuote(cart.items.map((item) => ({ item_type: "product", item_id: item.product_id, quantity: item.quantity })), drawerOpen);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error("Please sign in to check out");
      setDrawerOpen(false);
      navigate("/auth");
      return;
    }
    if (cart.items.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    if (!quoteState.quote || quoteState.loading) return;
    setCheckingOut(true);
    try {
      // Cart Pass-Through Checkout — ONE call for the whole cart. Server does
      // a single balance check on subtotal + tax:
      //   path=wallet_debit → every line bought atomically (all-or-nothing)
      //   path=stripe_topup → ONE Stripe session for the exact shortfall;
      //     /checkout/complete?item_type=cart finalises + clears the cart.
      const r = await axios.post(
        `${API}/wallet/cart-pass-through-checkout`,
        {
          items: cart.items.map((it) => ({ item_id: it.product_id, quantity: it.quantity })),
          expected_total_cents: quoteState.quote.total_cents,
          success_url: `${window.location.origin}/checkout/complete?item_type=cart`,
          cancel_url: window.location.href,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.path === "stripe_topup" && r.data?.checkout_url) {
        // Keep the cart in localStorage — it's cleared once the purchase lands.
        window.location.href = r.data.checkout_url;
        return;
      }
      const totalDebited = r.data?.total_paid_cents || 0;
      const purchasedCount = r.data?.item_count || cart.items.length;
      toast.success(`Cart complete — ${(totalDebited / 100).toFixed(2)} Tokens across ${purchasedCount} item${purchasedCount === 1 ? "" : "s"}`);
      clearCart?.();
      setDrawerOpen(false);
      navigate("/my-purchases");
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      quoteState.refresh();
      if (status === 428) {
        toast.error(typeof detail === "string" ? detail : "This artist isn't payout-ready yet. Please try again later.");
        return;
      }
      const msg = typeof detail === "string" ? detail : (detail?.message || "Cart checkout failed — nothing was charged");
      toast.error(msg);
    } finally {
      setCheckingOut(false);
    }
  };

  if (!drawerOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 z-[60] transition-opacity"
        onClick={() => setDrawerOpen(false)}
        data-testid="cart-drawer-backdrop"
      />

      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#050810] border-l border-[#1A2030] z-[70] flex flex-col shadow-2xl"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-4 border-b border-[#1A2030]">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-[#EC008C]" />
            <h2 className="font-['Unbounded'] text-lg font-bold text-white">
              Your Cart
            </h2>
            {itemCount > 0 && (
              <span
                className="text-xs bg-[#EC008C] text-black font-bold rounded-full w-6 h-6 flex items-center justify-center"
                data-testid="cart-drawer-count"
              >
                {itemCount}
              </span>
            )}
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            className="text-[#8A92A0] hover:text-white transition-colors"
            data-testid="cart-drawer-close"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Artist banner */}
        {cart.artist_name && cart.items.length > 0 && (
          <div className="px-6 py-3 bg-[#0A0E18] border-b border-[#1A2030]">
            <p className="text-xs text-[#8A92A0]">Shopping from</p>
            <Link
              to={`/artist/${cart.artist_id}`}
              onClick={() => setDrawerOpen(false)}
              className="text-white font-semibold hover:text-[#EC008C] transition-colors"
              data-testid="cart-drawer-artist"
            >
              {cart.artist_name}
            </Link>
          </div>
        )}

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.items.length === 0 ? (
            <div
              className="h-full flex flex-col items-center justify-center text-center"
              data-testid="cart-drawer-empty"
            >
              <ShoppingBag className="w-12 h-12 text-[#4A5262] mb-4" />
              <p className="text-white font-semibold mb-2">Your cart is empty</p>
              <p className="text-sm text-[#8A92A0]">
                Add products from any artist storefront to get started.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.items.map((it) => (
                <div
                  key={it.product_id}
                  className="flex gap-3 p-3 bg-[#0A0E18] rounded-lg border border-[#1A2030]"
                  data-testid={`cart-item-${it.product_id}`}
                >
                  {it.image_url && (
                    <img
                      src={it.image_url}
                      alt={it.name}
                      className="w-16 h-16 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-semibold truncate">
                      {it.name}
                    </p>
                    <p className="text-xs text-[#8A92A0] mt-0.5">
                      ${it.price.toFixed(2)}
                      {it.requires_shipping && (
                        <span className="ml-2 text-[#FF6BC5]">· ships</span>
                      )}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(it.product_id, it.quantity - 1)}
                        className="w-6 h-6 rounded bg-[#1A2030] text-white hover:bg-[#EC008C]/20 flex items-center justify-center"
                        data-testid={`cart-item-${it.product_id}-decrease`}
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span
                        className="text-sm text-white font-mono w-6 text-center"
                        data-testid={`cart-item-${it.product_id}-qty`}
                      >
                        {it.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(it.product_id, it.quantity + 1)}
                        className="w-6 h-6 rounded bg-[#1A2030] text-white hover:bg-[#EC008C]/20 flex items-center justify-center"
                        data-testid={`cart-item-${it.product_id}-increase`}
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button
                      onClick={() => removeItem(it.product_id)}
                      className="text-[#8A92A0] hover:text-[#F87171] transition-colors"
                      data-testid={`cart-item-${it.product_id}-remove`}
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <p className="text-sm text-white font-semibold">
                      ${(it.price * it.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.items.length > 0 && (
          <footer className="border-t border-[#1A2030] px-6 py-4 space-y-3">
            <QuoteSummary state={quoteState} prefix="cart" onNavigate={() => setDrawerOpen(false)} />
            <Button
              onClick={handleCheckout}
              disabled={checkingOut || !quoteState.quote || quoteState.loading}
              className="w-full bg-[#EC008C] text-black hover:bg-[#FF3399] font-semibold"
              data-testid="cart-checkout-btn"
            >
              {checkingOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…
                </>
              ) : (
                <>{quoteState.quote?.topup_cents ? `Add $${money(quoteState.quote.topup_cents)} and buy` : `Pay ${money(quoteState.quote?.total_cents)} Tokens`}</>
              )}
            </Button>
            <button
              onClick={clearCart}
              className="w-full text-xs text-[#8A92A0] hover:text-white transition-colors"
              data-testid="cart-clear-btn"
            >
              Clear cart
            </button>
          </footer>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
